/**
 * Built-in glyphs for the Specialisation Carousel (section 3005).
 *
 * Vector data is the exact export of the Figma vuesax symbols used in the
 * design (arrow-right bold, box bold, close-circle bold, export linear,
 * arrow-right linear). They are inlined rather than shipped as static SVG
 * files so every fill/stroke can take an admin-configured colour — each of
 * these is replaceable by an uploaded image in the CMS, in which case the
 * component is not rendered at all.
 */

interface GlyphProps {
  /** Fill / stroke colour driven by the CMS. */
  color?: string;
  className?: string;
}

/** vuesax/bold/arrow-right — filled circle with a right arrow knocked out. */
export const ArrowRightBoldIcon = ({
  color = "#292D32",
  className,
}: GlyphProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM16.03 12.53L13.03 15.53C12.88 15.68 12.69 15.75 12.5 15.75C12.31 15.75 12.12 15.68 11.97 15.53C11.68 15.24 11.68 14.76 11.97 14.47L13.69 12.75H8.5C8.09 12.75 7.75 12.41 7.75 12C7.75 11.59 8.09 11.25 8.5 11.25H13.69L11.97 9.53C11.68 9.24 11.68 8.76 11.97 8.47C12.26 8.18 12.74 8.18 13.03 8.47L16.03 11.47C16.32 11.76 16.32 12.24 16.03 12.53Z"
      fill={color}
    />
  </svg>
);

/** vuesax/bold/box — default placeholder icon for the variant_2 (icon) card. */
export const BoxBoldIcon = ({ color = "#292D32", className }: GlyphProps) => (
  <svg
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M53.8929 20.8532L33.3596 32.7465C32.5329 33.2265 31.4929 33.2265 30.6396 32.7465L10.1063 20.8532C8.6396 19.9998 8.26626 17.9998 9.38626 16.7465C10.1596 15.8665 11.0396 15.1465 11.9729 14.6398L26.4263 6.63984C29.5196 4.90651 34.5329 4.90651 37.6263 6.63984L52.0796 14.6398C53.0129 15.1465 53.8929 15.8932 54.6663 16.7465C55.7329 17.9998 55.3596 19.9998 53.8929 20.8532Z"
      fill={color}
    />
    <path
      d="M30.4796 37.7065V55.8932C30.4796 57.9198 28.4263 59.2532 26.613 58.3732C21.1196 55.6798 11.8663 50.6398 11.8663 50.6398C8.61296 48.7998 5.94629 44.1598 5.94629 40.3465V26.5865C5.94629 24.4798 8.15962 23.1465 9.97296 24.1865L29.1463 35.3065C29.9463 35.8132 30.4796 36.7198 30.4796 37.7065Z"
      fill={color}
    />
    <path
      d="M33.5195 37.7065V55.8932C33.5195 57.9198 35.5729 59.2532 37.3862 58.3732C42.8795 55.6798 52.1329 50.6398 52.1329 50.6398C55.3862 48.7998 58.0529 44.1598 58.0529 40.3465V26.5865C58.0529 24.4798 55.8395 23.1465 54.0262 24.1865L34.8529 35.3065C34.0529 35.8132 33.5195 36.7198 33.5195 37.7065Z"
      fill={color}
    />
  </svg>
);

/** vuesax/bold/close-circle — modal dismiss button. */
export const CloseCircleBoldIcon = ({
  color = "#9F1C34",
  className,
}: GlyphProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM15.36 14.3C15.65 14.59 15.65 15.07 15.36 15.36C15.21 15.51 15.02 15.58 14.83 15.58C14.64 15.58 14.45 15.51 14.3 15.36L12 13.06L9.7 15.36C9.55 15.51 9.36 15.58 9.17 15.58C8.98 15.58 8.79 15.51 8.64 15.36C8.35 15.07 8.35 14.59 8.64 14.3L10.94 12L8.64 9.7C8.35 9.41 8.35 8.93 8.64 8.64C8.93 8.35 9.41 8.35 9.7 8.64L12 10.94L14.3 8.64C14.59 8.35 15.07 8.35 15.36 8.64C15.65 8.93 15.65 9.41 15.36 9.7L13.06 12L15.36 14.3Z"
      fill={color}
    />
  </svg>
);

/** vuesax/linear/export — trailing glyph on the "Apply Now" CTA. */
export const ExportLinearIcon = ({
  color = "#FFFFFF",
  className,
}: GlyphProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12"
      stroke={color}
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13 11L21.2 2.8"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22 6.83V2H17.17"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * vuesax/linear/arrow-right rotated 90° — the download glyph on the modal's
 * brochure CTA (the design reuses the arrow rather than a dedicated icon).
 */
export const DownloadArrowIcon = ({
  color = "#000000",
  className,
}: GlyphProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={{ transform: "rotate(90deg)" }}
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M14.43 5.93L20.5 12L14.43 18.07"
      stroke={color}
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.5 12H20.33"
      stroke={color}
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
